import {
  LoadingOutlined,
  LockOutlined,
  LogoutOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import adminAvatar from "assets/images/admin.png";
import UserAvatar from "assets/images/default-avatar.png";
import { AuthenticationContext } from "context/AuthenticationContext";
import ChangePasswordModal from "features/changePassword/presentation/view/ChangePasswordModal";
import ls from "localstorage-slim";
import { isEmpty, isNull } from "lodash";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { DataFormat } from "utils/format/DataFormat";
import { strings } from "utils/localizedStrings";
import Transition from "utils/Transition";
ls.config.encrypt = true;

const UserMenu = () => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [openChangePwd, setOpenChangePwd] = useState<boolean>(false);
  const [featureLoading, setFeatureLoading] = useState<boolean>(false);
  const { user, info, runAppStartUp, resetState } = AuthenticationContext();
  const trigger = useRef(null);
  const dropdown = useRef(null);
  const location = useLocation();
  let token = DataFormat.getToken();

  const clickHandler = ({ target }) => {
    if (
      !dropdownOpen ||
      dropdown.current?.contains(target) ||
      trigger.current?.contains(target)
    )
      return;
    setDropdownOpen(false);
  };

  // close on click outside
  useEffect(() => {
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  const onLogout = () => {
    setDropdownOpen(!dropdownOpen);
    localStorage.clear();
    sessionStorage.clear();
    ls.clear();
    resetState();
  };

  const renderInfo = () => {
    if (!!token) {
      return (
        <>
          {user?.isAdmin
            ? "Admin"
            : user?.isModerator
              ? `${info[0]?.toUpperCase() + info?.slice(1)}`
              : !isEmpty(user?.partner) && !isNull(user?.partner)
                ? `Đối tác: ${user?.partner}`
                : ""}
        </>
      );
    }
  };

  useEffect(() => {
    if (!!token) {
      renderInfo();
    }
  }, []);

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className="inline-flex justify-center items-center group"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <img
          className="w-8 h-8 rounded-full"
          src={user?.isAdmin ? adminAvatar : UserAvatar}
          width="32"
          height="32"
          alt="User"
        />
        <div className="flex items-center truncate">
          <span className="truncate ml-2 text-sm font-medium group-hover:text-gray-800">
            {user?.name || user?.username || ""}
          </span>
          <svg
            className="w-3 h-3 flex-shrink-0 ml-1 fill-current text-gray-400"
            viewBox="0 0 12 12"
          >
            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
          </svg>
        </div>
      </button>

      <Transition
        className="origin-top-right z-10 absolute top-full rounded-[15px] right-0 min-w-60 bg-white border border-gray-200 py-1.5 shadow-lg overflow-hidden mt-2"
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        appear={undefined}
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div className="pt-0.5 pb-2 px-3 mb-1 flex flex-col gap-1">
            <div className="font-medium text-gray-800">{user?.name}</div>
            <div className="text-xs text-gray-500 italic">{renderInfo()}</div>
            <div className="text-xs text-gray-500 italic">
              {strings.UserManagement.Columns.Phone}: {user?.phone}
            </div>
            <div className="text-xs text-gray-500 italic">
              {strings.UserManagement.Columns.Email}: {user?.email}
            </div>
            <div className="text-xs text-gray-500 italic">
              {strings.UserManagement.Columns.Role}:{" "}
              {user?.role?.name || strings.GlobalLabels.NoInfo}
            </div>
          </div>
          <ul className="flex flex-col justify-center items-start w-full">
            <li>
              <Button
                type="link"
                size="middle"
                style={{
                  color: "black",
                }}
                icon={<LockOutlined className="text-black" />}
                className="font-medium text-sm flex items-center py-1 px-3 w-full text-left"
                onClick={() => {
                  setDropdownOpen(false);
                  setOpenChangePwd(true);
                }}
              >
                {strings.ChangePassword.Title}
              </Button>
            </li>
            <li>
              <Button
                type="link"
                size="middle"
                style={{ color: "black" }}
                icon={
                  featureLoading ? (
                    <LoadingOutlined className="text-black" />
                  ) : (
                    <RedoOutlined className="text-black" />
                  )
                }
                className="font-medium text-sm flex items-center py-1 px-3 w-full text-left"
                onClick={async () => {
                  setFeatureLoading(true);
                  await runAppStartUp({
                    isForceRefresh: true,
                    pathname: location?.pathname,
                    taskKeys: [
                      "load-featute",
                      "load-user-menu",
                      "build-sidebar",
                    ],
                  }).finally(() => {
                    setFeatureLoading(false);
                    setDropdownOpen(false);
                  });
                }}
              >
                {strings.GlobalLabels.RefreshFeature}
              </Button>
            </li>
            <li className="border-t-[1px] border-t-gray-200 w-full mt-1 flex justify-start items-center">
              <Button
                type="link"
                size="middle"
                className="font-medium text-sm flex items-center py-1 px-3"
                style={{ color: "#dc2626" }}
                icon={<LogoutOutlined className="text-red-600" />}
              >
                <Link to="/login" onClick={onLogout}>
                  {strings.GlobalLabels.Logout}
                </Link>
              </Button>
            </li>
          </ul>
        </div>
      </Transition>
      {openChangePwd && (
        <ChangePasswordModal
          open={openChangePwd}
          onCancel={() => setOpenChangePwd(false)}
        />
      )}
    </div>
  );
};

export default UserMenu;
